import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSalesRecap,
  createSalesRecap,
  updateSalesRecap,
  deleteSalesRecap,
  calculateRevenueShare,
  createInvoiceFromRecap,
} from "libs/api/sales_recap";
import {
  getAllSalesRecapSchedules,
  submitSalesRecapData,
  finalizeSalesRecapData,
  deleteSalesRecapSchedule,
} from "libs/api/contract_sales_recap";


// ── Get Sales Recap ───────────────────────────────────────────────────────────
export const getSalesRecapThunk = createAsyncThunk(
  "salesRecap/get",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname = "", queryParams = "" }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getSalesRecap(pathname, queryParams);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

// ── Create Sales Recap ────────────────────────────────────────────────────────
export const createSalesRecapThunk = createAsyncThunk(
  "salesRecap/create",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, pathname = "/create", body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await createSalesRecap(pathname, body);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

// ── Update Sales Recap ────────────────────────────────────────────────────────
export const updateSalesRecapThunk = createAsyncThunk(
  "salesRecap/update",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await updateSalesRecap(`/update/${id}`, body);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

// ── Delete Sales Recap ────────────────────────────────────────────────────────
export const deleteSalesRecapThunk = createAsyncThunk(
  "salesRecap/delete",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, id }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await deleteSalesRecap(`/delete/${id}`);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

// ── Calculate Revenue Share (Preview) ────────────────────────────────────────
export const calculateRevenueShareThunk = createAsyncThunk(
  "salesRecap/calculate",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await calculateRevenueShare(body);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

// ── Create Invoice From Recap ─────────────────────────────────────────────────
export const createInvoiceFromRecapThunk = createAsyncThunk(
  "salesRecap/createInvoice",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await createInvoiceFromRecap(body);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

// ── Contract Sales Recap Schedule Thunks ─────────────────────────────────────

/** GET /contract-sales-recap — semua jadwal rekap */
export const getAllSalesRecapSchedulesThunk = createAsyncThunk(
  "salesRecap/getSchedules",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, params = {} } = {}) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await getAllSalesRecapSchedules(params);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

/** PATCH /contract-sales-recap/:scheduleId/submit */
export const submitSalesRecapDataThunk = createAsyncThunk(
  "salesRecap/submitSchedule",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, scheduleId, body }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await submitSalesRecapData(scheduleId, body);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

/** PATCH /contract-sales-recap/:scheduleId/finalize */
export const finalizeSalesRecapDataThunk = createAsyncThunk(
  "salesRecap/finalizeSchedule",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, scheduleId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await finalizeSalesRecapData(scheduleId);
      typeof cbFulfilled === "function" && cbFulfilled(response?.data);
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);

/** DELETE /contract-sales-recap/:scheduleId */
export const deleteSalesRecapScheduleThunk = createAsyncThunk(
  "salesRecap/deleteSchedule",
  async ({ cbPending, cbFulfilled, cbRejected, cbFinally, scheduleId }) => {
    try {
      typeof cbPending === "function" && cbPending();
      const response = await deleteSalesRecapSchedule(scheduleId);
      typeof cbFulfilled === "function" && cbFulfilled();
      return response?.data;
    } catch (error) {
      if (error.response) {
        typeof cbRejected === "function" && cbRejected(error.response);
        throw JSON.stringify(error.response.data);
      } else {
        typeof cbRejected === "function" && cbRejected(error);
        throw JSON.stringify({ message: error.message });
      }
    } finally {
      typeof cbFinally === "function" && cbFinally();
    }
  }
);
